<hx-dialog title="Edit Column Options &amp; Rules">
  <div hxDialogContent>
    <div class="col-options-editor">
      <div class="col-options-editor__content">
        <hx-segmented-control
          [defaultSelectedKey]="activeTab()"
          (selectionChange)="setTab($event)"
          [widthMode]="'content-fit'"
          variant="underline">
          @for (col of columnsState(); track col.key) {
            <hx-segment [label]="col.label" [key]="col.key">
              <div class="col-options-editor__section">
                @if (col.hint) {
                  <p class="col-options-editor__hint">
                    {{ col.hint }}
                    @if (col.specialValue) {
                      Use value
                      <code>{{ col.specialValue }}</code>
                      to enable additional input.
                    }
                  </p>
                }

                <div class="col-options-editor__options-list">
                  @for (opt of col.options; track opt.value; let i = $index) {
                    <div class="col-options-editor__option-row">
                      <button
                        hxIconButton
                        type="button"
                        svgIcon="ccs-x"
                        variant="ghost"
                        size="sm"
                        context="neutral"
                        (click)="removeOption(col.key, i)"
                        aria-label="Remove option"></button>
                      <input
                        type="text"
                        class="col-options-editor__label-input"
                        [value]="opt.label"
                        (blur)="updateOptionLabel(col.key, i, $any($event.target).value)"
                        (keydown.enter)="$any($event.target).blur()"
                        placeholder="Option label" />
                      <span class="col-options-editor__value-badge">{{ opt.value }}</span>
                    </div>
                  }
                  @if (col.options.length === 0) {
                    <div class="col-options-editor__empty">No options defined.</div>
                  }
                </div>

                <button type="button" class="col-options-editor__add-btn" (click)="addOption(col.key)">
                  + Add Option
                </button>
              </div>
            </hx-segment>
          }

          <hx-segment label="Rules" key="__rules__">
            <div class="col-options-editor__section">
              <p class="col-options-editor__hint">
                Define dynamic rules to hide values or require values based on conditions.
              </p>

              @if (showValidationErrors() && validationErrors().length > 0) {
                <div class="col-options-editor__error-banner">
                  <button
                    hxIconButton
                    type="button"
                    svgIcon="ccs-x"
                    variant="ghost"
                    size="sm"
                    context="neutral"
                    (click)="dismissValidationErrors()"
                    aria-label="Dismiss"></button>
                  <div class="col-options-editor__error-content">
                    <strong>Cannot Save:</strong>
                    @for (error of validationErrors(); track $index) {
                      <div>{{ error }}</div>
                    }
                  </div>
                </div>
              }

              <div class="col-options-editor__unified-rules">
                @for (rule of rulesState(); track rule.id) {
                  <div class="col-options-editor__rule-row">
                    <div class="col-options-editor__rule-enabled">
                      <input
                        type="checkbox"
                        [checked]="rule.enabled"
                        (change)="toggleRuleEnabled(rule.id)"
                        title="Enable/disable this rule" />
                    </div>

                    <div class="col-options-editor__rule-content">
                      <div class="col-options-editor__rule-condition">
                        <span class="col-options-editor__rule-label">When</span>
                        <hx-dropdown
                          [options]="columnOptions()"
                          [value]="rule.when.column"
                          (valueChange)="updateRuleConditionColumn(rule.id, $any($event))"
                          class="col-options-editor__rule-dropdown"
                          size="sm"></hx-dropdown>

                        <hx-dropdown
                          [options]="getOperatorOptions(rule.when.column)"
                          [value]="rule.when.operator"
                          (valueChange)="updateRuleConditionOperator(rule.id, $any($event))"
                          class="col-options-editor__rule-dropdown"
                          size="sm"></hx-dropdown>

                        <hx-dropdown
                          [options]="getConditionValueOptions(rule)"
                          [value]="rule.when.values"
                          [multiple]="true"
                          (valueChange)="updateRuleConditionValues(rule.id, $any($event))"
                          class="col-options-editor__rule-dropdown"
                          placeholder="Select values..."
                          size="sm"></hx-dropdown>
                      </div>

                      <div class="col-options-editor__rule-action">
                        <span class="col-options-editor__rule-label">Then</span>
                        <hx-dropdown
                          [options]="actionTypeOptions()"
                          [value]="rule.then.type"
                          (valueChange)="updateRuleActionType(rule.id, $any($event))"
                          class="col-options-editor__rule-dropdown"
                          size="sm"></hx-dropdown>

                        <span class="col-options-editor__rule-label">in</span>
                        <hx-dropdown
                          [options]="getTargetColumnOptions(rule.then.type)"
                          [value]="rule.then.targetColumn || ''"
                          (valueChange)="updateRuleActionTargetColumn(rule.id, $any($event))"
                          class="col-options-editor__rule-dropdown"
                          size="sm"></hx-dropdown>

                        @if (rule.then.targetColumn) {
                          <hx-dropdown
                            [options]="getActionValueOptions(rule)"
                            [value]="rule.then.targetValues || []"
                            [multiple]="true"
                            (valueChange)="updateRuleActionValues(rule.id, $any($event))"
                            class="col-options-editor__rule-dropdown"
                            placeholder="Select values..."
                            size="sm"></hx-dropdown>
                        }
                      </div>
                    </div>

                    <div class="col-options-editor__rule-delete">
                      <button
                        hxIconButton
                        type="button"
                        svgIcon="ccs-x"
                        variant="ghost"
                        size="sm"
                        context="negative"
                        (click)="removeRule(rule.id)"
                        aria-label="Remove rule"></button>
                    </div>
                  </div>
                }
                @if (rulesState().length === 0) {
                  <div class="col-options-editor__empty">
                    No rules defined. All column values will always be visible and nothing will be required.
                  </div>
                }
              </div>
              <button type="button" class="col-options-editor__add-btn" (click)="addRule()">+ Add Rule</button>
            </div>
          </hx-segment>
        </hx-segmented-control>
      </div>
    </div>
  </div>

  <div hxDialogActions class="col-options-editor__footer">
    <button hxButton type="button" variant="outline" context="neutral" (click)="resetToDefaults()">
      Reset to Defaults
    </button>
    <div class="col-options-editor__footer-actions">
      <button hxButton type="button" variant="outline" context="neutral" (click)="cancel()">Cancel</button>
      <button hxButton type="button" variant="fill" context="positive" (click)="save()">Save</button>
    </div>
  </div>
</hx-dialog>
